import { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { useFormik } from 'formik';
import jwt_decode from 'jwt-decode';

import { AddEditApproverModal } from './ActionModals/AddEditApproverModal';
import { DeleteApproverConfirmationModal } from './ActionModals/DeleteApproverConfirmationModal';
import { ApproversTable } from './Components/ApproversTable';
import {
  getAllApprovers,
  createApprover,
  updateApprover,
  deleteApprover,
} from './Helpers/Api';
import {
  getValidationSchema,
  getInitialValues,
} from './Helpers/Config';

const ApproverManagement = () => {
  const dispatch = useDispatch();
  const [approvers, setApprovers] = useState([]);
  const [modal, setModal] = useState('');
  const [user, setUser] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  }, []);

  // Fetch approvers without depending on user state directly
  const fetchApprovers = useCallback(async (companyId) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const { approvers } = await getAllApprovers(companyId);
      setApprovers(approvers);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch approvers.',
      });
      setApprovers([]);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  const formik = useFormik({
    initialValues: getInitialValues(modal, selectedItem),
    validationSchema: getValidationSchema(modal),
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!user) {
        Swal.fire({
          icon: 'error',
          title: 'Authentication Error',
          text: 'User not authenticated. Please log in.',
        });
        setSubmitting(false);
        return;
      }

      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const approverData = {
          ...values,
          company_id: user?.companyId || user?.company_id || user?.id, // ensure company id is included
        };

        if (modal === 'add') {
          await createApprover(approverData);
          await Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Approver saved successfully!',
          });
        } else if (modal === 'edit') {
          await updateApprover(selectedItem._id, approverData);
          await Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Approver updated successfully!',
          });
        }

        const companyId = approverData.company_id;
        await fetchApprovers(companyId);
        closeModal();
        resetForm({ values: getInitialValues('', null) });
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: error.message });
        resetForm({ values: getInitialValues('', null) });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
        setSubmitting(false);
      }
    },
  });

  const openAddModal = () => {
    if (!user) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'User not authenticated. Please log in.',
      });
      return;
    }
    setSelectedItem(null);
    setModal('add');
  };

  const openEditModal = (item) => {
    if (!user) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'User not authenticated. Please log in.',
      });
      return;
    }
    setSelectedItem(item);
    setModal('edit');
  };

  const openDeleteModal = (item) => {
    if (!user) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'User not authenticated. Please log in.',
      });
      return;
    }
    setSelectedItem(item);
    setModal('delete');
  };

  const closeModal = () => {
    formik.resetForm({
      values: getInitialValues('', null),
    });
    setModal('');
    setSelectedItem(null);
  };

  const handleDeleteConfirm = async () => {
    if (!user) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'User not authenticated. Please log in.',
      });
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await deleteApprover(selectedItem._id);
      await Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: 'Approver deleted successfully!',
      });
      const companyId = user?.companyId || user?.company_id || user?.id;
      await fetchApprovers(companyId);
      closeModal();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Initialize user on mount
  useEffect(() => {
    const userData = getUserFromToken();
    setUser(userData);
  }, [getUserFromToken]);

  // Fetch approvers only when user changes
  useEffect(() => {
    if (user) {
      const companyId = user?.companyId || user?.company_id || user?.id;
      fetchApprovers(companyId);
    }
  }, [user, fetchApprovers]);

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Approver Management</title>
      </MetaTags>
      <div className="container-fluid">
        <Breadcrumbs title="Approvers" breadcrumbItem="Approver Management" />

        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-primary" onClick={openAddModal}>
            Add Approver
          </button>
        </div>

        <ApproversTable
          user={user}
          approvers={approvers}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
        />

        <AddEditApproverModal
          user={user}
          open={modal === 'add' || modal === 'edit'}
          mode={modal}
          formik={formik}
          onClose={closeModal}
          itemId={selectedItem?._id || ''}
        />

        <DeleteApproverConfirmationModal
          user={user}
          open={modal === 'delete'}
          onClose={closeModal}
          onDelete={handleDeleteConfirm}
          item={selectedItem}
        />
      </div>
    </div>
  );
};

export { ApproverManagement };